"use client";
import { useContext, useEffect, useState } from "react";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import Popup from "../Popup/Popup";
import styles from "./AstroHackAdPopup.module.scss";
import { AppContext } from "@/context/AppContext";
import { PopupType } from "@/utils/common.types";
import AstroHackPoster from "../../../images/Astro/AstroHack-poster.png";

const CLOSE_BTN_DELAY_MS = 10000;
const CLOSE_BTN_DELAY_SECONDS = CLOSE_BTN_DELAY_MS / 1000;
const REGISTER_URL = "https://shorturl.at/eohad";
const DOWNLOAD_URL = "https://ref.astrolive.app/referral/7tx61i9u";

interface AstroHackAdPopupProps extends PopupType {
  onRegisterClick?: () => void;
  onDownloadClick?: () => void;
  onContinueLoginClick?: () => void;
}

const AstroHackAdPopup = ({
  show,
  onClose,
  onRegisterClick = () => window.open(REGISTER_URL, "_blank", "noopener,noreferrer"),
  onDownloadClick = () => window.open(DOWNLOAD_URL, "_blank", "noopener,noreferrer"),
  onContinueLoginClick,
}: AstroHackAdPopupProps) => {
  const { setPopups, auth } = useContext(AppContext) ?? {};
  const isLoggedIn = !!auth?.token;
  const [showCloseBtn, setShowCloseBtn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(CLOSE_BTN_DELAY_SECONDS);

  useEffect(() => {
    if (!show) {
      setShowCloseBtn(false);
      setSecondsLeft(CLOSE_BTN_DELAY_SECONDS);
      return;
    }
    const timer = setTimeout(() => setShowCloseBtn(true), CLOSE_BTN_DELAY_MS);
    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [show]);

  const handleContinueLogin = () => {
    onClose();
    setPopups && setPopups({ showLoginPopup: true });
  };

  return (
    <Popup
      show={show}
      onClose={onClose}
      className={styles.popupWrapper}
      onClickOutside={false}
    >
      <div className={styles.astroHackWrapper}>
        {showCloseBtn ? (
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        ) : (
          <div className={styles.closeBtnTimer} aria-hidden="true">
            <svg viewBox="0 0 36 36" className={styles.closeBtnTimerSvg}>
              <circle
                className={styles.closeBtnTimerTrack}
                cx="18"
                cy="18"
                r="16"
              />
              <circle
                className={styles.closeBtnTimerProgress}
                cx="18"
                cy="18"
                r="16"
                strokeDasharray={2 * Math.PI * 16}
                strokeDashoffset={
                  2 *
                  Math.PI *
                  16 *
                  (1 - secondsLeft / CLOSE_BTN_DELAY_SECONDS)
                }
              />
            </svg>
            <span className={styles.closeBtnTimerText}>{secondsLeft}</span>
          </div>
        )}

        <div className={styles.visualPanel}>
          <div className={styles.heroImage}>
            <Image
              src={AstroHackPoster}
              alt="AstroHack 2026 poster"
              fill
              sizes="(max-width: 599px) 100vw, 384px"
              className={styles.heroPoster}
              priority
            />
          </div>

          <button
            type="button"
            className={styles.registerBtn}
            onClick={onRegisterClick}
          >
            <span className={styles.registerBtnIcon} aria-hidden="true">
              <ArrowRight size={18} strokeWidth={2.5} />
            </span>
            <span className={styles.registerBtnText}>Register</span>
          </button>
        </div>

        <div className={styles.contentPanel}>
          <h2 className={styles.heading}>
            AstroHack 2026: Build the Next Universe
          </h2>
          <p className={styles.subtitle}>A National Hackathon.</p>

          <div className={styles.descriptionCard}>
            <p>
              <li>Build live prototypes in tech, UX, and growth</li>
              <li>
                Direct access to <strong> Pre-Placement Offers</strong>
              </li>
              <li>
                Share in <strong>₹2 Lakhs</strong> reward pool
              </li>
              <li>
                Premium vouchers and <strong>certificates</strong> for valid
                projects
              </li>
            </p>
          </div>

          <button
            type="button"
            className={styles.downloadBtn}
            onClick={onDownloadClick}
          >
            Download AstroLive
          </button>
          {!isLoggedIn && (
            <button
              type="button"
              className={styles.continueLoginBtn}
              onClick={onContinueLoginClick ?? handleContinueLogin}
            >
              Continue Login
            </button>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default AstroHackAdPopup;
